import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { ActiveDealRow } from '../api/use-active-deals';
import { useAssignFinancingPartner } from '../api/use-assign-financing-partner';
import { FINANCING_PARTNERS, type FinancingPartner } from '../lib/financing-partners';

export function isKnownPartner(value: string | null): value is FinancingPartner {
  return value !== null && (FINANCING_PARTNERS as readonly string[]).includes(value);
}

function parseSoldDate(value: string | null): Date | null {
  return value === null ? null : new Date(`${value}T00:00:00.000Z`);
}

export function useAssignPartnerForm(
  row: ActiveDealRow | null,
  open: boolean,
  onOpenChange: (open: boolean) => void,
) {
  const mutation = useAssignFinancingPartner();
  const [partner, setPartner] = useState<FinancingPartner | null>(null);
  const [soldDate, setSoldDate] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Re-seed when the dialog opens against a different row so state doesn't
  // leak across rows.
  useEffect(() => {
    if (!open || row === null) return;
    setPartner(isKnownPartner(row.financing_partner) ? row.financing_partner : null);
    setSoldDate(parseSoldDate(row.sold_date));
    setErrorMessage(null);
  }, [open, row]);

  function submit(next: { partner: FinancingPartner | null; sold_date: Date | null }) {
    if (row === null) return;
    setErrorMessage(null);
    mutation.mutate(
      {
        blOrderId: row.id,
        financing_partner: next.partner,
        sold_date: next.sold_date,
      },
      {
        onSuccess: () => {
          toast.success(next.partner === null ? 'Partner cleared' : 'Partner assigned');
          onOpenChange(false);
        },
        onError: (err) => {
          setErrorMessage(err.message);
        },
      },
    );
  }

  return {
    partner,
    setPartner,
    soldDate,
    setSoldDate,
    errorMessage,
    isPending: mutation.isPending,
    handleSave: () => {
      submit({ partner, sold_date: soldDate });
    },
    handleClear: () => {
      submit({ partner: null, sold_date: null });
    },
  };
}
